import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type {
  AccountQrActiveResult,
  AccountQrIdentity,
  AccountQrIssueResult,
  ChangePasswordRequest,
  LoginRequest,
  LoginResult,
  OrgConfigResult,
  QrShareCreateResult,
  ScreenDeviceClaimResult,
  ScreenDeviceInfo,
  ScreenDeviceRequestInput,
  ScreenDeviceRequestResult,
  ScreenTokenRequest,
  ScreenTokenResult,
  SwitchOrgResult,
  SwitchableOrgResult,
  UpdateMyProfileRequest,
  UserProfile
} from '../types/auth';

export function createAuthApi(http: HttpClient) {
  return {
    /** 账号密码登录（sys_user.user_code 或手机号）。 */
    login(body: LoginRequest): Promise<LoginResult> {
      return http.request<LoginResult>({ path: '/auth/login', method: 'POST', body });
    },

    /** 当前用户信息（实时回库，含角色与权限码）。 */
    me(): Promise<UserProfile> {
      return http.request<UserProfile>({ path: '/auth/me' });
    },

    /**
     * 大屏换票：用扫码授权后领取的「设备凭据」换取短时效只读令牌（匿名端点，凭据本身即凭据）。
     * 建议由调用方在到期前自动续期，不要长期持有返回的 token。
     */
    screenToken(body: ScreenTokenRequest): Promise<ScreenTokenResult> {
      return http.request<ScreenTokenResult>({ path: '/auth/screen-token', method: 'POST', body });
    },

    /**
     * 大屏设备授权（扫码确认，匿名端点，屏端使用）：
     * request（只声明业务类型与大屏名称）→ 拿到申请码 → 屏端展示二维码 → 已登录的电脑端扫码确认 → claim 领取设备凭据。
     * 申请阶段不输任何租户/机构编码：显示谁的数据由扫码确认的人决定。
     */
    screenDevice: {
      /** 发起授权申请：返回申请码（屏端据此生成二维码）。 */
      request(body: ScreenDeviceRequestInput): Promise<ScreenDeviceRequestResult> {
        return http.request<ScreenDeviceRequestResult>({ path: '/auth/screen-device/request', method: 'POST', body });
      },
      /** 查看申请详情（确认页展示用；屏端也可用于判断状态）。 */
      inspect(code: string): Promise<ScreenDeviceInfo> {
        return http.request<ScreenDeviceInfo>({ path: `/auth/screen-device/${encodeURIComponent(code)}` });
      },
      /** 屏端领取设备凭据（仅「已确认」后首次领取会返回 deviceToken）。 */
      claim(code: string): Promise<ScreenDeviceClaimResult> {
        return http.request<ScreenDeviceClaimResult>({
          path: `/auth/screen-device/${encodeURIComponent(code)}/claim`,
          method: 'POST'
        });
      }
    },

    /**
     * 手机免密进入（电脑端已登录 → 二维码 → 手机打开即带同一登录态）：
     * create（电脑端签发，需登录）→ 手机浏览器打开 `…/m-enter?ticket=…` → redeem（匿名兑换）。
     * 票据一次性、默认 3 分钟有效，只能兑换签发人自己的登录态。
     */
    qrShare: {
      /** 电脑端签发一次性票据（需登录，签发人即手机端将进入的账号）。 */
      create(): Promise<QrShareCreateResult> {
        return http.request<QrShareCreateResult>({ path: '/auth/qr-share', method: 'POST' });
      },
      /** 手机端兑换登录态（匿名，凭票据本身；成功后票据立即作废）。 */
      redeem(ticket: string): Promise<LoginResult> {
        return http.request<LoginResult>({
          path: `/auth/qr-share/${encodeURIComponent(ticket)}/redeem`,
          method: 'POST'
        });
      }
    },

    /** 可切换机构清单（本院 + 任职机构；跨机构授权时含监管对象与层级下属）。 */
    switchableOrgs(): Promise<SwitchableOrgResult> {
      return http.request<SwitchableOrgResult>({ path: '/auth/orgs' });
    },

    /** 切换机构：返回新令牌（含目标 org_id），前端需整体替换本地令牌与身份。 */
    switchOrg(orgId: Id): Promise<SwitchOrgResult> {
      return http.request<SwitchOrgResult>({ path: '/auth/switch-org', method: 'POST', body: { orgId } });
    },

    /** 机构个性化配置（配色/标题/业务开关）；不传 orgId 取当前机构。 */
    orgConfig(orgId?: Id): Promise<OrgConfigResult> {
      return http.request<OrgConfigResult>({ path: '/auth/org-config', query: toQuery({ orgId }) });
    },

    /** 自助修改口令（校验原密码，成功后刷新 90 天有效期）。 */
    changePassword(body: ChangePasswordRequest): Promise<{ changed: boolean; pwdExpireAt: string }> {
      return http.request<{ changed: boolean; pwdExpireAt: string }>({
        path: '/auth/change-password',
        method: 'POST',
        body
      });
    },

    /** 自助维护个人信息（头像/邮箱），返回最新身份信息供前端刷新本地会话。 */
    updateProfile(body: UpdateMyProfileRequest): Promise<UserProfile> {
      return http.request<UserProfile>({ path: '/auth/profile', method: 'PUT', body });
    },

    /**
     * 账户身份二维码（院方/运营端为账户签发，App 扫码确认送达与签收）：
     * issue 重新签发（旧码立即作废）→ 展示 → 扫码端 resolve 验签+验期。
     * 明文码只在 issue 时返回一次，库里只存 sha256。
     */
    accountQr: {
      /** 为指定账户签发（或重新签发）30 天有效的身份码。 */
      issue(userId: Id): Promise<AccountQrIssueResult> {
        return http.request<AccountQrIssueResult>({ path: `/sys/user/${userId}/qrcode`, method: 'POST' });
      },
      /** 查询该账户当前有效码的状态（不含明文，用于展示"已签发/何时过期"）。 */
      get(userId: Id): Promise<AccountQrActiveResult | null> {
        return http.request<AccountQrActiveResult | null>({ path: `/sys/user/${userId}/qrcode` });
      },
      /** 吊销该账户当前有效码。 */
      revoke(userId: Id): Promise<{ revoked: number }> {
        return http.request<{ revoked: number }>({ path: `/sys/user/${userId}/qrcode`, method: 'DELETE' });
      },
      /** 扫码端解析（验签 + 验期 + 租户隔离）。 */
      resolve(code: string, terminal?: string): Promise<AccountQrIdentity> {
        return http.request<AccountQrIdentity>({
          path: '/auth/user-qrcode/resolve',
          method: 'POST',
          body: { code, terminal }
        });
      },
      /**
       * 本人自助：查看自己的当前有效码（仅需登录，无需 sys:user:manage）。
       * 明文 code 若已签发会回传，便于重复查看/打印工牌。
       */
      getMy(): Promise<AccountQrActiveResult | null> {
        return http.request<AccountQrActiveResult | null>({ path: '/auth/my-qrcode' });
      },
      /** 本人自助：签发（或重新签发）自己的身份码，旧码立即作废。 */
      issueMy(): Promise<AccountQrIssueResult> {
        return http.request<AccountQrIssueResult>({ path: '/auth/my-qrcode', method: 'POST' });
      },
      /** 本人自助：吊销自己的当前有效码。 */
      revokeMy(): Promise<{ revoked: number }> {
        return http.request<{ revoked: number }>({ path: '/auth/my-qrcode', method: 'DELETE' });
      }
    }
  };
}

export type AuthApi = ReturnType<typeof createAuthApi>;
